import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  CreditCard,
  Pause,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  createAsaasAccountSchema,
  formatBR,
  updateAsaasAccountSchema,
  type CreateAsaasAccountInput,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { AsaasAccountForm } from '../../components/asaas/AsaasAccountForm';
import { PillButton } from '../../components/clients/PillButton';
import { EmptyState as RichEmptyState } from '../../components/common/EmptyState';
import { PageHelp } from '../../components/common/PageHelp';
import {
  useAsaasAccounts,
  useCreateAsaasAccount,
  useDisableAsaasAccount,
  useUpdateAsaasAccount,
} from '../../hooks/use-asaas-accounts';
import { extractErrorMessage } from '../../lib/errors';
import type { AsaasAccountView } from '../../lib/asaas-accounts-api';
import { useAuthStore } from '../../stores/useAuthStore';

const STATUS_LABEL: Record<AsaasAccountView['status'], string> = {
  active: 'Ativa',
  paused: 'Pausada',
  disabled: 'Desativada',
};

const STATUS_DOT: Record<AsaasAccountView['status'], string> = {
  active: 'bg-good ring-good/15',
  paused: 'bg-warn ring-warn/15',
  disabled: 'bg-bad ring-bad/15',
};

export function AsaasAccountsPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'admin' || role === 'dev';

  const list = useAsaasAccounts();
  const create = useCreateAsaasAccount();
  const update = useUpdateAsaasAccount();
  const disable = useDisableAsaasAccount();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AsaasAccountView | null>(null);

  async function handleCreate(input: CreateAsaasAccountInput): Promise<void> {
    try {
      const parsed = createAsaasAccountSchema.parse(input);
      await create.mutateAsync(parsed);
      toast.success('Conta Asaas criada.');
      setFormOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos criar a conta.'));
    }
  }

  async function handleUpdate(input: CreateAsaasAccountInput): Promise<void> {
    if (!editing) return;
    try {
      const parsed = updateAsaasAccountSchema.parse(input);
      await update.mutateAsync({ id: editing.id, payload: parsed });
      toast.success('Conta Asaas atualizada.');
      setEditing(null);
      setFormOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos atualizar.'));
    }
  }

  async function handleDisable(account: AsaasAccountView): Promise<void> {
    if (account.isDefault) {
      toast.error('Não dá pra desativar a conta default.');
      return;
    }
    const ok = window.confirm(
      `Desativar "${account.name}"? Conta para de aceitar cobranças. Bloqueia se houver charges/payments existentes.`,
    );
    if (!ok) return;
    try {
      await disable.mutateAsync(account.id);
      toast.success('Conta desativada.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos desativar.'));
    }
  }

  function copyWebhook(url: string): void {
    void navigator.clipboard
      .writeText(url)
      .then(() => toast.success('URL do webhook copiada.'))
      .catch(() => toast.error('Não conseguimos copiar.'));
  }

  function openCreate(): void {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(account: AsaasAccountView): void {
    setEditing(account);
    setFormOpen(true);
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Contas Asaas</h1>
            <p className="mt-1 text-[13px] text-muted">
              Cobranças do Control Center roteiam por origem da assinatura: Master pra owl_managed,
              conta específica pra saas_direct.
            </p>
          </div>
          <PageHelp id="asaas-accounts" />
        </div>
        {canEdit && (
          <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={openCreate}>
            Nova conta
          </PillButton>
        )}
      </header>

      {list.isLoading ? (
        <SkeletonList />
      ) : list.isError ? (
        <ErrorBox
          message={extractErrorMessage(list.error, 'Não conseguimos carregar.')}
          onRetry={() => void list.refetch()}
        />
      ) : list.data && list.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-3">
          {list.data.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              canEdit={canEdit}
              onEdit={() => openEdit(account)}
              onDisable={() => void handleDisable(account)}
              onCopyWebhook={() => copyWebhook(account.webhook_url)}
            />
          ))}
        </div>
      ) : (
        <EmptyState canEdit={canEdit} onAdd={openCreate} />
      )}

      {canEdit && (
        <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
          <Dialog.Portal>
            <Dialog.Overlay
              className={cn(
                'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              )}
            />
            <Dialog.Content
              className={cn(
                'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
                'w-[min(720px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
                'rounded-[14px] border border-line bg-panel shadow-lg p-7',
                'focus:outline-none',
              )}
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                  {editing ? `Editar ${editing.name}` : 'Nova conta Asaas'}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Fechar"
                    className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                </Dialog.Close>
              </div>

              <AsaasAccountForm
                mode={editing ? 'edit' : 'create'}
                initial={
                  editing
                    ? {
                        name: editing.name,
                        environment: editing.environment,
                        webhook_path: editing.webhookPath,
                        is_default: editing.isDefault,
                        status: editing.status,
                      }
                    : undefined
                }
                apiKeyRequired={!editing}
                webhookSecretRequired={!editing}
                isPending={create.isPending || update.isPending}
                onSubmit={editing ? handleUpdate : handleCreate}
                onCancel={() => setFormOpen(false)}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

interface AccountCardProps {
  account: AsaasAccountView;
  canEdit: boolean;
  onEdit: () => void;
  onDisable: () => void;
  onCopyWebhook: () => void;
}

function AccountCard({
  account,
  canEdit,
  onEdit,
  onDisable,
  onCopyWebhook,
}: AccountCardProps): JSX.Element {
  return (
    <article className="rounded-[14px] border border-line bg-panel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'mt-1 flex h-9 w-9 items-center justify-center rounded-[10px]',
              account.isDefault ? 'bg-ink text-page' : 'bg-panel-2 text-muted',
            )}
          >
            {account.isDefault ? (
              <CheckCircle2 size={16} strokeWidth={1.8} />
            ) : (
              <Pause size={16} strokeWidth={1.8} />
            )}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="m-0 text-[16px] font-semibold text-ink">{account.name}</h2>
              {account.isDefault && (
                <span className="rounded-full bg-ink/10 px-2 py-[1px] text-[10px] font-medium text-ink">
                  default
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-muted-2">
              <span
                className={cn(
                  'inline-flex items-center gap-[6px] text-[11px] font-medium',
                  account.status === 'active' ? 'text-ink-2' : 'text-muted',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'h-[7px] w-[7px] rounded-full ring-[3px]',
                    STATUS_DOT[account.status],
                  )}
                />
                {STATUS_LABEL[account.status]}
              </span>
              <span>·</span>
              <span>{account.environment}</span>
              <span>·</span>
              <code className="font-mono">{account.webhookPath}</code>
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={`Editar ${account.name}`}
              onClick={onEdit}
              className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
            >
              <Pencil size={13} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              aria-label={`Desativar ${account.name}`}
              onClick={onDisable}
              disabled={account.isDefault || account.status === 'disabled'}
              className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-bad disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Trash2 size={13} strokeWidth={1.8} />
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 rounded-[10px] border border-line bg-panel-2 p-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-3">Webhook URL</div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <code className="overflow-x-auto whitespace-nowrap font-mono text-[12px] text-ink">
            {account.webhook_url}
          </code>
          <button
            type="button"
            aria-label="Copiar webhook URL"
            onClick={onCopyWebhook}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
          >
            <Copy size={12} strokeWidth={1.8} />
          </button>
        </div>
        <p className="mt-2 text-[11px] text-muted-2">
          Cole essa URL em <strong>Configurações &gt; Webhooks</strong> no painel Asaas dessa conta.
        </p>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
        <KeyDisplay label="API key" value={account.api_key_masked} />
        <KeyDisplay label="Webhook secret" value={account.webhook_secret_masked} />
        <KeyDisplay label="Criada" value={formatBR(account.createdAt)} />
      </div>
    </article>
  );
}

function KeyDisplay({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-3">{label}</dt>
      <dd className="mt-1 font-mono text-[12px] text-ink">{value}</dd>
    </div>
  );
}

function SkeletonList(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3">
      {[0, 1].map((i) => (
        <div
          key={i}
          aria-hidden
          className="h-[140px] animate-pulse rounded-[14px] border border-line bg-panel-2"
        />
      ))}
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
      <div className="flex items-center gap-2 font-medium text-bad">
        <AlertCircle size={14} strokeWidth={2} aria-hidden />
        Não conseguimos carregar.
      </div>
      <p className="mt-1 text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry}>Tentar de novo</PillButton>
      </div>
    </div>
  );
}

function EmptyState({ canEdit, onAdd }: { canEdit: boolean; onAdd: () => void }): JSX.Element {
  return (
    <RichEmptyState
      icon={CreditCard}
      title="Nenhuma conta Asaas conectada"
      description="Asaas é o gateway que processa Pix, boleto e cartão. Sem conta conectada, nenhuma cobrança sai."
      bullets={[
        'Cadastre 1 conta por CNPJ que vai faturar (Owl Mãe, Reservei, etc)',
        'Cada conta gera 1 webhook URL — você configura no painel Asaas',
        'A conta marcada como default é usada por planos sem conta específica',
      ]}
      primaryAction={
        canEdit
          ? {
              label: 'Cadastrar primeira conta',
              icon: <Plus size={14} strokeWidth={2} />,
              onClick: onAdd,
            }
          : undefined
      }
    />
  );
}
